import { EQ_TYPE_CHILLER, EQ_TYPE_HPCHILLER } from "./constant"
import { isString } from "lodash"

interface Property {
  targetKey: string
  value: string
}

interface EquipmentDetails {
  [key: string]: string[]
}

interface TransformChillerPropertiesResult {
  instance: string[]
  compCondType: string
  type: string
  equipmentDetails: EquipmentDetails
}

export const transformChillerProperties = (
  propertyArray: Property[],
  equipmentType: string
): TransformChillerPropertiesResult => {
  const equipmentDetails: EquipmentDetails = {}

  const componentPropertyArray = propertyArray?.filter(
    ({ targetKey, value }) =>
      targetKey?.toLowerCase()?.includes("compressorcountckt") &&
      typeof value === "string" &&
      parseInt(value) > 0
  )

  const filteredArray = Array.from(
    new Set(componentPropertyArray?.map((item) => item?.targetKey))
  ).map((targetKey) => ({
    targetKey,
    value: Math.max(
      ...componentPropertyArray
        ?.filter((item) => item?.targetKey === targetKey)
        ?.map((item) => parseInt(item?.value))
    )
  }))

  const circuitInstances =
    filteredArray?.length > 1
      ? filteredArray?.map(({ targetKey }) => {
          const cktcount = targetKey?.slice(targetKey?.length - 1)
          console.log(cktcount, "cktcount_checking_count")
          equipmentDetails[cktcount] = []
          return `Ckt${cktcount}`
        })
      : ["Ckt"]
  if (!(filteredArray?.length > 1)) {
    equipmentDetails[1] = []
  }
  console.log(
    {
      propertyArray,
      equipmentType,
      filteredArray,
      componentPropertyArray,
      circuitInstances
    },
    "transformChillerProperties_console"
  )

  const compressorInstances: string[] = []
  if (
    (filteredArray?.length === 1 && filteredArray[0]?.value <= 1) ||
    filteredArray?.length === 0
  ) {
    compressorInstances?.push("Comp")
    equipmentDetails[1] = ["A"]
  } else {
    filteredArray.forEach((item) => {
      const { targetKey, value } = item
      for (let i = 1; i <= value; i++) {
        const suffix = String.fromCharCode(i + 96).toUpperCase()
        const cktcount = targetKey?.slice(targetKey?.length - 1)
        compressorInstances?.push(`${cktcount}${suffix}`)
        // Initialize equipmentDetails[cktcount] if it doesn't exist
        if (!equipmentDetails[cktcount]) {
          equipmentDetails[cktcount] = []
        }
        equipmentDetails[cktcount].push(suffix)
      }
    })
  }

  let compCondType = ""
  let instancePrefix = "Chiller"

  if (equipmentType?.toLowerCase() === EQ_TYPE_CHILLER?.toLowerCase()) {
    const condenserType = (
      propertyArray?.filter(
        ({ targetKey }) => targetKey === "CondenserType"
      )?.[0]?.value || ""
    )
      ?.trim()
      ?.replace(/\s/g, "")
    const compressorType = (
      propertyArray?.filter(
        ({ targetKey }) => targetKey === "CompressorType"
      )?.[0]?.value || ""
    )
      ?.trim()
      ?.replace(/\s/g, "")
    const compressorMotorStarterType = (
      propertyArray?.filter(
        ({ targetKey }) => targetKey === "CompressorMotorStarterType"
      )?.[0]?.value || ""
    )
      ?.trim()
      ?.replace(/\s/g, "")
    const bearingType = (
      propertyArray?.filter(({ targetKey }) => targetKey === "BearingType")?.[0]
        ?.value || ""
    )
      ?.trim()
      ?.replace(/\s/g, "")

    compCondType =
      condenserType && compressorType
        ? `${condenserType}#${compressorType}#${
            compressorMotorStarterType || ""
          }#${compressorType === "Centrifugal" ? bearingType || "" : ""}`
        : ""
  } else if (
    equipmentType?.toLowerCase() === EQ_TYPE_HPCHILLER?.toLowerCase()
  ) {
    compCondType =
      (propertyArray || [])?.find(
        (c) => c?.targetKey === "DistributionSystemType"
      )?.value || ""
    instancePrefix = "HeatPumpChiller"
  }

  return {
    instance: [instancePrefix, ...circuitInstances, ...compressorInstances],
    compCondType: compCondType,
    type: equipmentType,
    equipmentDetails
  }
}

// dynamic update of properties for multiple circuit and compressor
// allProperties
// equipmentDetails = {
//   Ckt:[Comp] // represents single circuit with single compressor - name,tisDisplayName #and$ values are removed, other properties are updated considering {1:"A"}
//   1: ["A", "B"], // ckt:[compressor]
//   2: ["B"]
// }
export const transformDynamicPropertyMetaData = (
  allProperties,
  equipmentDetails
) => {
  const bandAxesProperties = []

  allProperties.forEach((property) => {
    if (
      property?.targetKey &&
      (property?.targetKey.includes("_m_") ||
        property?.targetKey.includes("_n_") ||
        property?.targetKey.includes("_o_"))
    ) {
      const bands = [1, 2, 3, 4, 5, 6, 7, 8]
      const axes = ["X", "Y", "Z"]
      const modes = [1, 2, 3, 4, 5, 6]
      if (property) {
        if (
          property?.targetKey.includes("_m_") ||
          property?.targetKey.includes("_n_")
        ) {
          bands.forEach((band) => {
            axes.forEach((axis) => {
              const newProperty = {
                ...property,
                id: property.id
                  .replace("_m_", `${band}`)
                  .replace("_n_", `${axis}`),
                targetKey: property.targetKey
                  .replace("_m_", `${band}`)
                  .replace("_n_", `${axis}`),
                name: property.name
                  .replace("_m_", `${band}`)
                  .replace("_n_", `${axis}`),
                value: property.value
                  .replace("_m_", `${band}`)
                  .replace("_n_", `${axis}`)
              }
              bandAxesProperties.push(newProperty)
            })
          })
        } else {
          modes.forEach((mode) => {
            const newProperty = {
              ...property,
              id: property.id.replace("_o_", `${mode}`),
              targetKey: property.targetKey.replace("_o_", `${mode}`),
              name: property.name.replace("_o_", `${mode}`),
              value: property.value.replace("_o_", `${mode}`)
            }
            bandAxesProperties.push(newProperty)
          })
        }
      }
    } else {
      bandAxesProperties.push(property)
    }
  })

  const dynamicMetaData = bandAxesProperties.reduce((accumulator, property) => {
    if (property.id && property.id.includes("#")) {
      const cktKeys = equipmentDetails ? Object.keys(equipmentDetails) : []
      if (cktKeys.length === 0) {
        // No equipment details — strip "#" and "$" and push as-is
        accumulator.push({
          ...property,
          id: property.id?.replace(/#|\$/g, ""),
          targetKey: property.targetKey?.replace(/#|\$/g, ""),
          name: property.name?.replace(/#|\$/g, ""),
          tisDisplayName: property.tisDisplayName?.replace(/#|\$/g, "")
        })
      } else {
        for (const ckt in equipmentDetails) {
          const circuit = ckt == "Ckt" ? "1" : ckt
          const transformCircuit = JSON.parse(JSON.stringify(property))
          if (transformCircuit.id) {
            transformCircuit.id = transformCircuit.id.replace("#", circuit)
          }
          if (transformCircuit.targetKey) {
            transformCircuit.targetKey = transformCircuit.targetKey.replace(
              "#",
              circuit
            )
          }
          if (transformCircuit.name) {
            transformCircuit.name =
              ckt == "Ckt"
                ? transformCircuit.name.replace("#", "")
                : transformCircuit.name.replace("#", circuit)
          }
          if (transformCircuit.tisDisplayName) {
            transformCircuit.tisDisplayName =
              ckt == "Ckt"
                ? transformCircuit.name.replace("#", "")
                : transformCircuit.tisDisplayName.replace("#", circuit)
          }
          if (transformCircuit.id && transformCircuit.id.includes("$")) {
            if (equipmentDetails[ckt].length === 0) {
              // No compressors — strip "$" and push circuit-level property
              accumulator.push({
                ...transformCircuit,
                id: transformCircuit.id?.replace(/\$/g, ""),
                targetKey: transformCircuit.targetKey?.replace(/\$/g, ""),
                name: transformCircuit.name?.replace(/\$/g, ""),
                tisDisplayName: transformCircuit.tisDisplayName?.replace(
                  /\$/g,
                  ""
                )
              })
            } else {
              for (const comp of equipmentDetails[ckt]) {
                const compressor = comp == "Comp" ? "A" : comp
                const transformCompressor = JSON.parse(
                  JSON.stringify(transformCircuit)
                )
                if (transformCompressor.id) {
                  transformCompressor.id = transformCompressor.id.replace(
                    "$",
                    compressor?.charCodeAt() - 64
                  )
                }
                if (transformCompressor.targetKey) {
                  transformCompressor.targetKey =
                    transformCompressor.targetKey.replace("$", compressor)
                }
                if (transformCompressor.tisDisplayName) {
                  transformCompressor.tisDisplayName =
                    comp == "Comp"
                      ? transformCompressor.tisDisplayName.replace("$", "")
                      : transformCompressor.tisDisplayName.replace(
                          "$",
                          compressor
                        )
                }
                if (transformCompressor.name) {
                  transformCompressor.name =
                    comp == "Comp"
                      ? transformCompressor.name.replace("$", "")
                      : transformCompressor.name.replace("$", compressor)
                }
                accumulator.push(transformCompressor)
              }
            }
          } else {
            accumulator.push(transformCircuit)
          }
        }
      }
    } else {
      accumulator.push(property)
    }
    return accumulator
  }, [])
  return dynamicMetaData
}

export const getEquipmentDetails = (selectedEquipmentObjects: any[]) => {
  const result: any = {}
  selectedEquipmentObjects?.forEach((equipmentObj) => {
    let instance = equipmentObj?.instance
    // Convert string to array if needed
    if (instance && isString(instance) && instance.startsWith("[")) {
      try {
        instance = JSON.parse(instance.replace(/'/g, '"'))
      } catch (e) {
        console.error("Failed to parse instance string:", e)
        return
      }
    }

    if (!instance || !Array.isArray(instance)) return

    // console.log("Processing instance:", instance)

    // Check if it's the default case: Ckt + Comp without numbers
    const hasCkt = instance.some((x: any) => x === "Ckt")
    const hasComp = instance.some((x: any) => x === "Comp")
    const hasNumberedCkt = instance.some((x: any) => /^Ckt\d+/.test(x))
    const hasNumberedCompressors = instance.some((x: any) =>
      /^\d+[A-Z]/.test(x)
    )

    if (hasCkt && hasComp && !hasNumberedCkt && !hasNumberedCompressors) {
      // Default case: ['Chiller', 'Ckt', 'Comp'] -> {'Ckt': ['Comp']}
      result["Ckt"] = ["Comp"]
    } else {
      // Step 1: Find circuit numbers from Ckt1, Ckt2, etc.
      let circuitNumbers = instance
        .filter((x: any) => /^Ckt\d+/.test(x)) // Match "Ckt" followed by digits
        .map((x: any) => x.match(/^Ckt(\d+)/)?.[1]) // Extract just the number part
        .filter(Boolean)

      // Step 2: If no Ckt numbers found, extract circuit numbers from items starting with digits
      if (circuitNumbers.length === 0) {
        circuitNumbers = [
          ...new Set(
            instance
              .filter((x: any) => /^\d/.test(x)) // Starts with a digit
              .map((x: any) => x.match(/^(\d+)/)?.[1]) // Extract leading digits
              .filter(Boolean)
          )
        ]
      }

      // Step 3: Build object with circuit numbers as keys (if not already present)
      circuitNumbers.forEach((k) => {
        if (k !== null && k !== "" && !result[k]) {
          result[k] = []
        }
      })

      // Step 4: Fill arrays with component suffixes
      instance.forEach((item: any) => {
        circuitNumbers.forEach((key: any) => {
          // Match items starting with circuit number (but not "Ckt" items)
          const regex = new RegExp(`^${key}([A-Z]+)`)
          if (!item.startsWith("Ckt") && regex.test(item)) {
            const suffix = item.match(regex)?.[1]
            if (suffix && result[key] && !result[key].includes(suffix)) {
              result[key].push(suffix)
            }
          }
        })
      })
    }
  })
  // console.log("Final equipment details:", result)
  return result
}

